import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { ArrowSquareOut, X } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Button } from '~/components/ui/button';
import { useChatOpenStore } from '~/lib/stores/chat-open-store';
import { isHomeRoute } from '~/lib/chat-origin';
import { useCancelLiveCallJob, useLiveCallJobs, type LiveCallJob, type LiveCallJobState } from '~/hooks/queries/use-live-call-jobs';

/**
 * FRONT-2991: one row per job the call handed to EDDY, under the call strip.
 * A row says the job's short name and where it is; a job that waits on the
 * user (a plan or a question) carries what the call read out. Rows can be
 * stopped while open and closed once finished. A follow-up still waiting on
 * its job sits under that job's row instead of a row of its own.
 */

const MAX_ROWS = 4;

const STATE_LABEL: Record<LiveCallJobState, string> = {
  queued: 'Queued',
  working: 'Working',
  'needs-you': 'Needs you',
  done: 'Done',
  failed: "Didn't go through",
  cancelled: 'Cancelled',
};

const CHIP_CLASS: Record<LiveCallJobState, string> = {
  queued: 'bg-muted text-muted-foreground',
  working: 'bg-[var(--eddy-blue-soft)] text-[var(--eddy-blue-text)]',
  'needs-you': 'bg-amber-500/15 text-amber-700 dark:text-amber-400',
  done: 'bg-success/15 text-success',
  failed: 'bg-destructive/15 text-destructive',
  cancelled: 'bg-muted text-muted-foreground',
};

const OPEN_STATES = new Set<LiveCallJobState>(['queued', 'working', 'needs-you']);

/** What the row says next to the name. */
export function jobMeta(job: LiveCallJob): string {
  if (job.state === 'queued') return job.startsAfter ? `Starts after: ${job.startsAfter}` : 'Starting…';
  if (job.state === 'working') {
    if (job.chatSessionId) return 'In a new chat';
    return job.followUpOf ? `Follow-up to ${job.followUpOf}` : 'EDDY is on it';
  }
  if (job.state === 'needs-you') return 'Waiting for your answer';
  if (job.state === 'cancelled') return 'Stopped';
  return job.result ?? '';
}

/** Rows to show: follow-ups still waiting on an open job nest under it; newest last, at most four. */
export function arrangeJobRows(jobs: LiveCallJob[], closed: ReadonlySet<string>): {
  rows: Array<{ job: LiveCallJob; followUps: LiveCallJob[] }>;
  hidden: number;
} {
  const visible = jobs.filter((j) => !closed.has(j.id));
  const openNames = new Set(visible.filter((j) => OPEN_STATES.has(j.state)).map((j) => j.name));
  const nested = (j: LiveCallJob) => j.state === 'queued' && !!j.followUpOf && openNames.has(j.followUpOf) && j.followUpOf !== j.name;
  const top = visible.filter((j) => !nested(j));
  const rows = top.map((job) => ({ job, followUps: visible.filter((f) => nested(f) && f.followUpOf === job.name) }));
  const hidden = Math.max(0, rows.length - MAX_ROWS);
  return { rows: rows.slice(hidden), hidden };
}

export interface CallJobRowsProps {
  jobs: LiveCallJob[];
  closed: ReadonlySet<string>;
  stoppingId: string | null;
  onStop: (job: LiveCallJob) => void;
  onClose: (job: LiveCallJob) => void;
  onOpenChat: (sessionId: string | null) => void;
}

export function CallJobRows({ jobs, closed, stoppingId, onStop, onClose, onOpenChat }: CallJobRowsProps) {
  const { rows, hidden } = arrangeJobRows(jobs, closed);
  if (rows.length === 0) return null;
  return (
    <div className="mt-1.5 grid grid-cols-[minmax(0,1fr)] gap-1" data-testid="hands-free-call-jobs" aria-live="polite">
      {hidden > 0 && (
        <button
          type="button"
          onClick={() => onOpenChat(null)}
          className="relative justify-self-start rounded-md px-2.5 py-0.5 text-xs text-muted-foreground transition-[color,scale] duration-150 after:absolute after:-inset-y-2.5 after:inset-x-0 after:content-[''] hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--eddy-blue)] active:scale-[0.96]"
          data-testid="hands-free-call-jobs-more"
        >
          {hidden} earlier {hidden === 1 ? 'job' : 'jobs'} in the chat
        </button>
      )}
      {rows.map(({ job, followUps }) => (
        <div
          key={job.id}
          data-testid="hands-free-call-job"
          data-state={job.state}
          className={`min-w-0 rounded-lg bg-card px-3 py-1.5 animate-in fade-in slide-in-from-top-1 duration-150 motion-reduce:animate-none ${
            job.state === 'needs-you'
              ? 'shadow-[0_0_0_1px_rgb(245_158_11/0.45),var(--shadow-sm)]'
              : 'shadow-[0_0_0_1px_var(--line),var(--shadow-sm)]'
          }`}
        >
          <div className="flex min-w-0 flex-wrap items-center gap-x-2.5 gap-y-1 sm:flex-nowrap">
            <span
              className={`inline-flex h-[22px] shrink-0 items-center gap-1.5 rounded-full px-2 text-[11.5px] font-medium ${CHIP_CLASS[job.state]}`}
              data-testid="hands-free-call-job-state"
            >
              <span
                className={`size-1.5 rounded-full bg-current ${job.state === 'working' ? 'animate-pulse motion-reduce:animate-none' : ''}`}
                aria-hidden
              />
              {STATE_LABEL[job.state]}
            </span>
            <span
              className={`min-w-0 flex-1 truncate text-sm font-medium sm:flex-none ${job.state === 'cancelled' ? 'text-muted-foreground line-through' : ''}`}
              data-testid="hands-free-call-job-name"
            >
              {job.name}
            </span>
            {job.state !== 'needs-you' && (
              <span
                className="order-last min-w-0 flex-1 basis-full truncate text-xs text-muted-foreground sm:order-none sm:basis-auto"
                data-testid="hands-free-call-job-meta"
              >
                {jobMeta(job)}
              </span>
            )}
            {job.state === 'needs-you' && <span className="hidden flex-1 sm:block" />}
            {job.chatSessionId && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="relative after:absolute after:-inset-2 after:content-[''] h-6 gap-1 px-1.5 text-xs text-[var(--eddy-blue-text)] active:scale-[0.96]"
                onClick={() => onOpenChat(job.chatSessionId)}
                data-testid="hands-free-call-job-open"
              >
                Open chat <ArrowSquareOut size={12} aria-hidden />
              </Button>
            )}
            {OPEN_STATES.has(job.state) ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="relative after:absolute after:-inset-2 after:content-[''] h-6 px-2 text-xs active:scale-[0.96]"
                disabled={stoppingId === job.id}
                onClick={() => onStop(job)}
                tooltip="Stop this job. Nothing it already did is undone."
                data-testid="hands-free-call-job-stop"
              >
                Stop
              </Button>
            ) : (
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                className="relative size-6 after:absolute after:-inset-2 after:content-[''] active:scale-[0.96]"
                onClick={() => onClose(job)}
                aria-label={`Close ${job.name}`}
                data-testid="hands-free-call-job-close"
              >
                <X size={12} />
              </Button>
            )}
          </div>
          {followUps.length > 0 && (
            <div className="mt-1 grid gap-0.5 border-l-2 border-[var(--line)] pl-3 text-xs text-muted-foreground" data-testid="hands-free-call-job-followups">
              {followUps.map((f) => (
                <span key={f.id} className="truncate">
                  Then: <span className="font-medium text-foreground/80">{f.name}</span>
                </span>
              ))}
            </div>
          )}
          {job.state === 'needs-you' && (
            <div className="mt-1.5 grid gap-1.5 border-t border-[var(--line)] pt-1.5" data-testid="hands-free-call-job-card">
              {job.result && <p className="text-sm text-pretty">{job.result}</p>}
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  type="button"
                  size="sm"
                  className="h-7 active:scale-[0.96]"
                  onClick={() => onOpenChat(job.chatSessionId)}
                  data-testid="hands-free-call-job-answer"
                >
                  Answer in the chat
                </Button>
                <span className="text-xs text-muted-foreground">Or just say your answer.</span>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

/**
 * Stop and close for the rows of the call running in `sessionId` — shared by
 * the desktop strip and the phone's call bar (FRONT-3268). Closed rows are
 * hidden for this mount only; Stop is the same cancel the call does by voice.
 */
export function useCallJobActions(sessionId: string) {
  const cancel = useCancelLiveCallJob(sessionId);
  const [closed, setClosed] = useState<ReadonlySet<string>>(() => new Set());
  const [stoppingId, setStoppingId] = useState<string | null>(null);
  return {
    closed,
    stoppingId,
    onStop: (job: LiveCallJob) => {
      setStoppingId(job.id);
      cancel.mutate(job.id, {
        onSuccess: (res) => {
          if (res.message) toast.info(res.message);
        },
        onError: () => toast.error("That didn't stop. Try again, or say it on the call."),
        onSettled: () => setStoppingId(null),
      });
    },
    onClose: (job: LiveCallJob) => setClosed((prev) => new Set([...prev, job.id])),
  };
}

/** The live rows for the call running in `sessionId`. */
export function HandsFreeCallJobs({
  sessionId,
  active,
  openStartingChat,
}: {
  sessionId: string;
  active: boolean;
  openStartingChat: () => void;
}) {
  const { data: jobs = [] } = useLiveCallJobs(sessionId, active);
  const actions = useCallJobActions(sessionId);
  const requestOpenChat = useChatOpenStore((s) => s.requestOpenChat);
  const navigate = useNavigate();
  const location = useLocation();

  return (
    <CallJobRows
      jobs={jobs}
      closed={actions.closed}
      stoppingId={actions.stoppingId}
      onStop={actions.onStop}
      onClose={actions.onClose}
      onOpenChat={(chatId) => {
        if (!chatId) return openStartingChat();
        if (!isHomeRoute(location.pathname)) navigate('/');
        requestOpenChat(chatId, null, null);
      }}
    />
  );
}
